<template>
  <div class="type-body-sm space-y-8">
    <div class="rich-text">
      <p>
        Thanks for signing the Wait Mate pledge for
        <strong>{{ props.name }}</strong> at
        <strong
          >{{ props.stats.school.name }},
          {{ props.stats.school.location.city }}</strong
        >
      </p>
    </div>
    <div class="space-y-8 rounded-xl bg-yellow p-8">
      <div>
        <h3 class="type-heading-5 mb-2.5">
          <span
            v-if="isPledgeUnlocked"
            v-text="
              globalsStore.settings?.pledge.cohortCard.heading.pledgeUnlocked
            "
          />
          <span v-else-if="totalStudents > 1">
            {{
              `${totalStudents} ${globalsStore.settings?.pledge.cohortCard.heading.multipleSignUps}`
            }}{{
              totalStudents >= 8
                ? ` ${
                    globalsStore.settings?.pledge.cohortCard.heading
                      .closeToThreshold
                  }`
                : ''
            }}
          </span>
          <span
            v-else
            v-text="
              globalsStore.settings?.pledge.cohortCard.heading.firstSignUp
            "
          />
        </h3>
        <BaseProgressBar :progress="progress" />
        <div class="mt-2.5">
          <p
            v-if="isPledgeUnlocked"
            v-text="
              globalsStore.settings?.pledge.cohortCard.progress.pledgeUnlocked
            "
          />
          <p v-else>
            {{
              pluralise(
                remainingStudents,
                'more family is',
                'more families are',
              )
            }}
            needed to unlock the pledge.
          </p>
        </div>
      </div>
      <div class="space-y-4">
        <p class="font-medium">
          <span
            v-if="isPledgeUnlocked"
            v-text="
              globalsStore.settings?.pledge.cohortCard.sharing.pledgeUnlocked
            "
          />
          <span
            v-else-if="totalStudents > 1"
            v-text="
              globalsStore.settings?.pledge.cohortCard.sharing.multipleSignUps
            "
          />
          <span
            v-else
            v-text="
              globalsStore.settings?.pledge.cohortCard.sharing.firstSignUp
            "
          />
        </p>
        <div class="flex flex-col space-y-2.5">
          <BaseButton class="btn btn-sm btn-black" @click="onCopyShareLink">
            {{ hasCopied ? 'Copied!' : 'Copy share link' }}
          </BaseButton>
          <NuxtLink
            class="btn btn-sm btn-black"
            to="https://donate.stripe.com/aEU6rw6GK5mzdEs5kn"
            target="_blank"
          >
            Support the movement
          </NuxtLink>
        </div>
        <div class="space-y-2.5">
          <h4 class="font-medium">Follow us</h4>
          <div class="space-x-2.5">
            <SocialButton to="https://www.instagram.com/wait.mate/">
              <SvgGlyphInstagram />
            </SocialButton>
            <SocialButton
              to="https://www.facebook.com/people/Wait-Mate/61559910075995/"
            >
              <SvgGlyphFacebook />
            </SocialButton>
          </div>
        </div>
      </div>
    </div>
    <div class="space-y-8 md:space-y-6">
      <h3 class="type-heading-5">What happens next?</h3>
      <div class="type-body-sm rich-text">
        <PayloadRichText
          :content="
            isPledgeUnlocked
              ? globalsStore.settings?.pledge.whatHappensNext.afterUnlock
              : globalsStore.settings?.pledge.whatHappensNext.beforeUnlock
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CohortStats } from './types'

interface CohortCardProps {
  name: string
  stats: CohortStats
}

const props = defineProps<CohortCardProps>()

const config = useRuntimeConfig()
const globalsStore = useGlobalsStore()

const unlockThresholdCount = 10

const totalStudents = computed(() => props.stats.studentCount)
const isPledgeUnlocked = computed(() => totalStudents.value >= 10)

const remainingStudents = computed(() =>
  Math.max(0, unlockThresholdCount - props.stats.studentCount),
)

const progress = computed(() => totalStudents.value / unlockThresholdCount)

/**
 * Copy Share Link
 */
let timeoutID: NodeJS.Timeout
const hasCopied = ref(false)
const onCopyShareLink = () => {
  if (timeoutID) {
    clearTimeout(timeoutID)
  }

  hasCopied.value = true
  copyToClipboard(config.public.siteUrl)

  timeoutID = setTimeout(() => {
    hasCopied.value = false
  }, 1000)
}
</script>
